import React, { useState, useEffect } from 'react';
import { Building2, Plus, Trash2, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface PreferredPrime {
  id?: string;
  company_name: string;
  created_at?: string;
}

export function PreferredPrimes() {
  const { user } = useAuth();
  const [primes, setPrimes] = useState<PreferredPrime[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPrimes();
  }, [user]);

  const loadPrimes = async () => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('preferred_primes')
        .select('*')
        .eq('profile_id', user.id)
        .order('company_name', { ascending: true });

      if (error) throw error;
      setPrimes(data || []);
    } catch (error) {
      console.error('Error loading preferred primes:', error);
      toast.error('Failed to load preferred primes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrime = () => {
    setPrimes(prev => [...prev, {
      company_name: ''
    }]);
  };

  const handleRemovePrime = async (index: number) => {
    try {
      const prime = primes[index];
      if (prime.id) {
        const { error } = await supabase
          .from('preferred_primes')
          .delete()
          .eq('id', prime.id);

        if (error) throw error;
      }

      setPrimes(prev => prev.filter((_, i) => i !== index));
      toast.success('Prime contractor removed successfully');
    } catch (error) {
      console.error('Error removing prime contractor:', error);
      toast.error('Failed to remove prime contractor');
    }
  };

  const handleChange = (index: number, value: string) => {
    setPrimes(prev => prev.map((prime, i) => 
      i === index ? { ...prime, company_name: value } : prime
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);

      // Validate required fields
      const hasEmptyRequired = primes.some(prime => !prime.company_name.trim());
      if (hasEmptyRequired) {
        toast.error('Please fill in all company names');
        return;
      }

      // Check for duplicates
      const names = primes.map(p => p.company_name.trim().toLowerCase());
      if (new Set(names).size !== names.length) {
        toast.error('Duplicate company names are not allowed');
        return;
      }

      // Update existing and create new primes
      const promises = primes.map(prime => {
        const data = {
          profile_id: user!.id,
          company_name: prime.company_name.trim()
        };

        if (prime.id) {
          return supabase
            .from('preferred_primes')
            .update(data)
            .eq('id', prime.id);
        } else {
          return supabase
            .from('preferred_primes')
            .insert([data]);
        }
      });

      const results = await Promise.all(promises);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to save some prime contractors');
      }

      await loadPrimes();
      toast.success('Preferred prime contractors updated successfully');
    } catch (error) {
      console.error('Error saving preferred primes:', error);
      toast.error('Failed to save preferred prime contractors');
    } finally {
      setSaving(false);
    }
  };

  const filteredPrimes = primes.filter(prime =>
    prime.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="bg-bw-surface rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-bw-surface rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-bw-muted" />
            Preferred Prime Contractors
          </h3>
          <p className="text-sm text-bw-muted mt-1">
            Manage your list of preferred prime contractors for teaming opportunities.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddPrime}
          className="flex items-center px-3 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Prime
        </button>
      </div>

      {primes.length > 0 && (
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-bw-muted" />
            <input
              type="text"
              placeholder="Search prime contractors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 block w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
            />
          </div>
        </div>
      )}

      <div className="space-y-4">
        {filteredPrimes.map((prime) => {
          // Map back to the index in the UNFILTERED `primes` array — the change/remove
          // handlers operate on that array, so passing the filtered index corrupted /
          // deleted the wrong record whenever a search filter was active.
          const originalIndex = primes.indexOf(prime);
          return (
          <div key={prime.id || originalIndex} className="flex items-center gap-4">
            <div className="relative flex-1">
              <Building2 className="absolute left-3 top-2.5 h-5 w-5 text-bw-muted" />
              <input
                type="text"
                value={prime.company_name}
                onChange={(e) => handleChange(originalIndex, e.target.value)}
                className="block w-full pl-10 rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                placeholder="Enter company name"
                required
              />
            </div>
            <button
              type="button"
              onClick={() => handleRemovePrime(originalIndex)}
              className="p-2 text-urgent hover:text-urgent"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
          );
        })}

        {primes.length === 0 && (
          <div className="text-center py-6 bg-bw-surface-subtle rounded-lg">
            <Building2 className="h-12 w-12 text-bw-muted mx-auto mb-3" />
            <p className="text-bw-muted">No preferred prime contractors added yet</p>
            <button
              type="button"
              onClick={handleAddPrime}
              className="mt-2 text-bw-primary hover:text-bw-primary"
            >
              Add your first prime contractor
            </button>
          </div>
        )}

        {primes.length > 0 && (
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bw-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </div>
    </form>
  );
}